import React from "react";

import { styled } from "styled-components";

import { colorTheme, gridSpacing } from "@utils";

import { describeQueryFallback } from "./describeQuery";
import type { FilterDescription } from "./FilterModalProps";
import StatusPill from "../../StatusPill/StatusPill";

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gridSpacing[2]}px;

  & + & {
    margin-top: ${gridSpacing[3]}px;
  }
`;

const Heading = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${colorTheme("neutralL1")};
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${gridSpacing[2]}px;
`;

const Muted = styled.div`
  font-size: 14px;
  color: ${colorTheme("neutralL1")};
`;

const Lines = styled.ul`
  margin: 0;
  padding-left: ${gridSpacing[4]}px;
  font-size: 14px;
  color: ${colorTheme("neutral")};

  li + li {
    margin-top: ${gridSpacing[1]}px;
  }
`;

/** What applying a view does: its filters, and on tables that save layouts (`describeLayout` given), its layout */
const ViewSummary = ({
  query,
  tableState,
  describeQuery = describeQueryFallback,
  describeLayout,
}: {
  query: string;
  tableState?: object | null;
  describeQuery?: (query: string) => FilterDescription[];
  describeLayout?: (layout: object) => string[];
}) => {
  const filters = describeQuery(query).filter((filter) => filter.values.length);
  const layout = tableState && describeLayout ? describeLayout(tableState) : [];

  return (
    <div data-testid="view-summary">
      <Section>
        <Heading>Filters</Heading>
        {filters.length ? (
          <Pills>
            {filters.map((filter) => (
              <StatusPill
                key={filter.key}
                text={`${filter.label}: ${filter.values.join(", ")}`}
              />
            ))}
          </Pills>
        ) : (
          <Muted>No filters, so every row shows</Muted>
        )}
      </Section>
      {describeLayout && (
        <Section>
          <Heading>Table layout</Heading>
          {!tableState ? (
            <Muted>
              Not saved. Columns, sort and rows per page stay as each person has
              them
            </Muted>
          ) : layout.length ? (
            <Lines>
              {layout.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </Lines>
          ) : (
            <Muted>Saved columns, sort and rows per page</Muted>
          )}
        </Section>
      )}
    </div>
  );
};

export default ViewSummary;
